/**
 * The Windows app (artifacts/chat-desktop) loads this same console in its own
 * window and offers three extra calls: a taskbar unread badge, a real Windows
 * notification and opening links in the default browser. In a browser none
 * of this exists and every function here quietly does nothing.
 */

type Invoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;

function invoker(): Invoke | null {
  const internals = (globalThis as { __TAURI_INTERNALS__?: { invoke?: Invoke } }).__TAURI_INTERNALS__;
  return typeof internals?.invoke === "function" ? internals.invoke : null;
}

export function isDesktopApp(): boolean {
  return invoker() !== null;
}

function call(cmd: string, args: Record<string, unknown>): void {
  const invoke = invoker();
  if (!invoke) return;
  invoke(cmd, args).catch(() => undefined);
}

let lastUnread = -1;
export function desktopUnread(count: number): void {
  if (count === lastUnread) return;
  lastUnread = count;
  call("set_unread", { count: Math.max(0, Math.floor(count)) });
}

export function desktopNotify(title: string, body: string): void {
  call("notify", { title, body });
}

/**
 * A link meant for a new tab would otherwise try to open a second app window.
 * Same-site links (the console's own pages) are left to the page.
 */
export function installDesktopLinks(): void {
  if (!isDesktopApp()) return;
  document.addEventListener(
    "click",
    (event) => {
      const link = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link) return;
      const url = new URL(link.href, location.href);
      const external = url.origin !== location.origin || link.target === "_blank";
      if (!external || !/^(https?|mailto):$/.test(url.protocol)) return;
      if (url.origin === location.origin && link.hasAttribute("download")) return;
      event.preventDefault();
      call("open_external", { url: url.href });
    },
    true,
  );
}

/** Where the Windows installer is published, next to the console. */
export const DESKTOP_DOWNLOAD_URL = "/download/AgentlyDesk-Setup.exe";
